import { Flex } from '@/layouts/Flex';
import { Colors } from '@/plugins/data/colors';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import styles from './styles.module.scss';

export interface IOption {
  key: string;
  label: string;
  description?: string;
  image?: string;
}

interface Props {
  options: IOption[];
  value: IOption | null;
  onChange: (selected: IOption | null) => void;
  placeholder?: string;
  emptyMessage?: string;
}

const MIN_AMOUNT_FOR_SEARCH = 10;

export function Dropdown({
  options,
  value,
  onChange,
  placeholder = 'Selecciona una opción',
  emptyMessage = 'No se encontraron resultados'
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = search
    ? options.filter((opt) => {
        const term = search.toLowerCase();
        return opt.label.toLowerCase().includes(term) || (opt.description?.toLowerCase().includes(term) ?? false);
      })
    : options;

  const handleSelect = (option: IOption) => {
    onChange(option);
    setIsOpen(false);
    setSearch('');
  };

  const resetSelection = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onChange(null);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || options.length <= MIN_AMOUNT_FOR_SEARCH) return;
    const id = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isOpen, options.length]);

  const renderSelected = () => {
    if (!value)
      return (
        <Flex alignItems='center' justifyContent='space-between' style={{ height: '100%' }}>
          {placeholder}
          <Icon name='chevronDown' size={18} />
        </Flex>
      );
    return (
      <div className={styles.dropdownSelectedSingle}>
        <div>
          {value.image && <img src={value.image} loading='lazy' alt={value.label} />}
          <span>{value.label}</span>
        </div>

        {value && (
          <div onClick={resetSelection} className={styles.resetBtn} role='button' aria-label='Reset selection'>
            <Icon name='close' size={20} color={Colors.primaryDark} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.dropdown} ref={ref} role='combobox' aria-haspopup='listbox' aria-expanded={isOpen}>
      <div
        className={styles.dropdownHeader}
        onClick={() => setIsOpen((v) => !v)}
        role='button'
        aria-haspopup='listbox'
        aria-expanded={isOpen}>
        {renderSelected()}
      </div>
      {isOpen && (
        <div className={styles.dropdownMenu}>
          {options.length > MIN_AMOUNT_FOR_SEARCH && (
            <input
              ref={searchRef}
              className={styles.dropdownSearch}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Buscar...'
            />
          )}
          <div>
            {filteredOptions.length === 0 ? (
              <div className={styles.dropdownEmpty}>
                {' '}
                {search ? `No hay resultados para "${search}"` : emptyMessage}{' '}
              </div>
            ) : (
              filteredOptions.map((opt) => (
                <div
                  key={opt.key}
                  className={styles.dropdownItem}
                  onClick={() => handleSelect(opt)}
                  role='option'
                  aria-selected={value?.key === opt.key}>
                  <div>
                    {opt.image && (
                      <img className={styles.dropdownImage} src={opt.image} loading='lazy' alt={opt.label} />
                    )}
                    <div>
                      <div className={styles.dropdownLabel}>{opt.label}</div>
                      {opt.description && <div className={styles.dropdownDescription}>{opt.description}</div>}
                    </div>
                  </div>
                  {value?.key === opt.key && <Icon name='circleCheck' size={20} color={Colors.primary} />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
